import React from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { posts } from '../data/blog';
import { vizComponents } from '../posts/components';

function readingTime(text: string): number {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

const fade = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

const Page = styled(motion.article)`
  min-height: 100vh;
  padding: 9rem 3rem 6rem;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 7rem 1.25rem 4rem;
  }
`;

const BackLink = styled(Link)`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  display: inline-block;
  margin-bottom: 2.5rem;

  &:hover {
    color: var(--accent);
  }
`;

const PostMeta = styled.div`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  text-transform: uppercase;
  margin-bottom: 0.85rem;

  span + span::before {
    content: '·';
    margin: 0 0.6rem;
  }
`;

const PostTitle = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(2.25rem, 4.5vw, 3.4rem);
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.01em;
  margin-bottom: 1.5rem;
`;

const Lede = styled.div`
  font-family: var(--font-display);
  font-size: clamp(1.1rem, 1.6vw, 1.3rem);
  font-style: italic;
  font-weight: 300;
  line-height: 1.55;
  color: var(--text-muted);
  max-width: 720px;
  padding-left: 1rem;
  border-left: 2px solid var(--accent);
  margin-bottom: 3rem;

  p { margin: 0; }
  p + p { margin-top: 1.25rem; }
`;

const Prose = styled.div`
  font-family: var(--font-body);
  font-size: 1rem;
  line-height: 1.75;
  color: var(--text);

  /* Headings */
  h1, h2, h3, h4 {
    font-family: var(--font-display);
    font-weight: 600;
    color: var(--text);
    line-height: 1.25;
  }
  h1 {
    font-size: 2.1rem;
    margin: 3.5rem 0 1.5rem;
    padding-bottom: 0.6rem;
    border-bottom: 2px solid var(--text);
  }
  h2 {
    font-size: 1.65rem;
    margin: 3rem 0 1rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid var(--border);
  }
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 2.25rem 0 0.85rem;
    padding-left: 0.75rem;
    border-left: 3px solid var(--accent);
  }

  /* Body text */
  p {
    margin: 1rem 0 1.4rem;
  }

  strong { font-weight: 600; }
  em { font-style: italic; }

  a {
    color: var(--accent);
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease;
  }
  a:hover {
    border-bottom-color: var(--accent);
  }

  /* Lists */
  ul, ol {
    margin: 1rem 0 1.4rem;
    padding-left: 1.6rem;
  }
  li {
    margin-bottom: 0.4rem;
  }

  /* Inline code */
  code {
    font-family: var(--font-mono);
    font-size: 0.86em;
    background: var(--bg-alt);
    padding: 0.1em 0.35em;
    border-radius: 3px;
  }

  /* Code blocks */
  pre {
    margin: 1.5rem 0;
    padding: 1.1rem 1.3rem;
    background: #1A1A1A;
    color: #E8E4DC;
    border-radius: 4px;
    overflow-x: auto;
    font-family: var(--font-mono);
    font-size: 0.82rem;
    line-height: 1.55;
  }
  pre code {
    background: transparent;
    padding: 0;
    color: inherit;
    font-size: inherit;
  }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.75rem 0;
    font-size: 0.88rem;
  }
  thead th {
    text-align: left;
    font-family: var(--font-mono);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    font-weight: 500;
    padding: 0.5rem 0.75rem;
    border-bottom: 1.5px solid var(--text);
  }
  tbody td {
    padding: 0.55rem 0.75rem;
    border-bottom: 1px solid var(--border);
  }
  th[align='right'], td[align='right'] {
    text-align: right;
  }

  /* Blockquote */
  blockquote {
    margin: 1.75rem 0;
    padding: 0.5rem 0 0.5rem 1.25rem;
    border-left: 3px solid var(--accent);
    color: var(--text-muted);
    font-style: italic;
  }
  blockquote p {
    margin: 0.5rem 0;
  }

  /* Horizontal rule */
  hr {
    border: none;
    border-top: 1px solid var(--border);
    margin: 4rem 0;
  }

  /* Score breakdown stacked bar */
  .score-bar {
    display: flex;
    width: 100%;
    height: 56px;
    margin: 1.5rem 0 2.5rem;
    border-radius: 4px;
    overflow: hidden;
    font-family: var(--font-mono);
    font-size: 0.72rem;
    color: #fff;

    .seg {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 0.4rem;
      gap: 0.15rem;
      text-align: center;
      letter-spacing: 0.04em;
      transition: filter 0.2s ease;

      &:hover { filter: brightness(1.08); }
    }
    .seg-rate { background: #C23028; }
    .seg-seg  { background: #8A2520; }
    .seg-pose { background: #5C1814; }

    .seg-label {
      text-transform: uppercase;
      font-weight: 500;
      opacity: 0.85;
    }
    .seg-value {
      font-size: 0.95rem;
      font-weight: 500;
    }
  }

  /* KaTeX math */
  .katex-display {
    margin: 1.5rem 0;
    text-align: center;
    overflow-x: auto;
    overflow-y: hidden;
  }
  .katex-display > .katex {
    display: inline-block;
    text-align: center;
  }
  .katex {
    font-size: 1.05em;
  }

  /* Visualization-TODO placeholder (working drafts only) */
  .viz-todo {
    margin: 1.5rem 0;
    padding: 1rem 1.25rem;
    background: repeating-linear-gradient(
      45deg,
      rgba(194, 48, 40, 0.04),
      rgba(194, 48, 40, 0.04) 10px,
      rgba(194, 48, 40, 0.08) 10px,
      rgba(194, 48, 40, 0.08) 20px
    );
    border: 1px dashed var(--accent);
    border-radius: 4px;
    font-size: 0.85rem;
    color: var(--text-muted);
    line-height: 1.55;

    strong {
      color: var(--accent);
      font-family: var(--font-mono);
      font-size: 0.7rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
  }

  /* Callout: flashing-lights warning */
  .flash-warning {
    margin: 1.75rem 0 1rem;
    padding: 0.85rem 1.1rem;
    border: 1px solid var(--accent);
    border-radius: 4px;
    background: rgba(194, 48, 40, 0.06);
    font-size: 0.88rem;
    color: var(--text);
    line-height: 1.5;

    strong {
      color: var(--accent);
      letter-spacing: 0.02em;
    }
  }

  /* Media */
  img, video {
    display: block;
    width: 100%;
    height: auto;
    margin: 2rem 0;
    border-radius: 4px;
    background: var(--bg-alt);
  }
`;

const NotFound = styled.div`
  font-family: var(--font-display);
  font-size: 1.4rem;
  text-align: center;
  margin-top: 4rem;
  color: var(--text-muted);
`;

interface PostMetadata {
  slug: string;
  title: string;
  date: string;
  tag: string;
  excerpt?: string;
  lede?: string;
}

interface LoadedPost {
  meta: PostMetadata;
  content: string;
}

const postLoaders: Record<string, () => Promise<LoadedPost>> = {
  'comma-compression': () =>
    import('../posts/comma-compression').then((m) => ({
      meta: m.meta as PostMetadata,
      content: m.content,
    })),
};

const BlogPost: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [loaded, setLoaded] = React.useState<LoadedPost | null>(null);
  const [missing, setMissing] = React.useState(false);

  const listingMeta = posts.find((p) => p.slug === slug);

  React.useEffect(() => {
    const loader = postLoaders[slug];
    if (!loader) {
      setMissing(true);
      return;
    }
    loader().then(setLoaded);
  }, [slug]);

  return (
    <Page initial="initial" animate="animate" exit="exit" variants={fade} transition={{ duration: 0.5 }}>
      <BackLink to="/blog">← Back to blog</BackLink>

      {missing && <NotFound>Post not found.</NotFound>}

      {loaded && (
        <>
          <PostMeta>
            <span>{loaded.meta.date}</span>
            {(loaded.meta.tag || listingMeta?.tag) && (
              <span>{loaded.meta.tag || listingMeta?.tag}</span>
            )}
            <span>{readingTime(loaded.content)} min read</span>
          </PostMeta>
          <PostTitle>{loaded.meta.title}</PostTitle>
          {loaded.meta.lede && (
            <Lede>
              {loaded.meta.lede.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </Lede>
          )}
          <Prose>
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeRaw, rehypeKatex]}
              components={{
                div: ({ node, ...props }) => {
                  const dataViz = (props as { 'data-viz'?: string })['data-viz'];
                  if (dataViz && vizComponents[dataViz]) {
                    const Comp = vizComponents[dataViz];
                    const source = (props as { 'data-source'?: string })['data-source'];
                    return <Comp source={source} />;
                  }
                  return <div {...props} />;
                },
              }}
            >
              {loaded.content}
            </ReactMarkdown>
          </Prose>
        </>
      )}
    </Page>
  );
};

export default BlogPost;
